import { createConnectionFromProposal, testAIConnection } from './api.js';
import constants from 'Constants.js';
import eventBus from 'EventBus';

class AIConnectionBuilder {

    /**
     * Creates a connection from an AI proposal via backend endpoint.
     * @param {Object} proposal - The connection proposal from the AI (action.data)
     * @param {Function} onProgress - Callback for progress updates
     * @returns {Promise<{connectionId: string, connectionName: string, actionsCreated: number, actionIds: string[]}>}
     */
    async createConnection(proposal, onProgress = () => {}) {
        onProgress('Creating connection and actions...');

        const result = await createConnectionFromProposal(proposal);

        // Refresh the correct connections tree based on type
        const refreshMap = {
            'soap': [constants.WEB_GROUP_TYPE, constants.CONNECTION_WEB_SERVICES_CONTAINER, 'webservice'],
            'database': [constants.DATABASES_GROUP_TYPE, constants.CONNECTION_DATABASES_CONTAINER, 'databases'],
            'textfile': [constants.TEXT_GROUP_TYPE, constants.CONNECTION_TEXT_CONTAINER, 'text'],
            'ldap': [constants.DIRECTORY_GROUP_TYPE, constants.CONNECTION_DIRECTORY_SERVICES_CONTAINER, 'directory'],
            'apps': [constants.FORMS_GROUP_TYPE, constants.CONNECTION_FORMS_CONTAINER, 'forms'],
            'rest': [constants.REST_GROUP_TYPE, constants.CONNECTION_REST_CONTAINER, 'rest'],
            'vectordb': [constants.VECTORDB_GROUP_TYPE, constants.CONNECTION_VECTORDB_CONTAINER, 'vectordb']
        };
        const [groupType, container, section] = refreshMap[proposal.connectionType] || refreshMap['rest'];
        eventBus.trigger(constants.EV_REFRESH_CONNECTION_NODES, groupType, container, section);

        return result;
    }

    /**
     * Tests an AI-created connection by calling each action and validating/fixing receive params.
     * @param {Object} proposal - The original proposal data (with actions, sendParams, receiveParams)
     * @param {Object} createResult - The result from createConnection ({connectionId, actionIds})
     * @param {Function} onProgress - Callback for progress updates
     * @returns {Promise<Object>} Test results from backend
     */
    async testConnection(proposal, createResult, onProgress = () => {}, testCredentials = null) {
        onProgress('Preparing test requests...');

        const token = sessionStorage.getItem('loginToken');

        const testRequest = {
            token: token,
            connectionType: proposal.connectionType || 'rest',
            connectionId: createResult.connectionId,
            connectionUrl: proposal.url,
            username: '',
            password: '',
            wsdlUrl: proposal.wsdlUrl || '',
            targetNamespace: proposal.targetNamespace || '',
            actions: (proposal.actions || []).map((action, index) => ({
                actionId: createResult.actionIds[index],
                actionName: action.name,
                endpoint: action.endpoint,
                method: action.method,
                operation: action.operation || '',
                requestType: action.requestType,
                responseType: action.responseType,
                sendParams: (action.sendParams || []).map(p => ({
                    name: p.name,
                    placement: p.placement,
                    sampleValue: p.sampleValue || p.fixedValue || '',
                    isFixed: p.isFixed,
                    fixedValue: p.fixedValue
                })),
                receiveParams: (action.receiveParams || []).map(p => ({
                    name: p.name,
                    path: p.path || p.name
                }))
            }))
        };

        if (testCredentials) {
            testRequest.testCredentials = testCredentials;
        }

        onProgress('Testing actions against live API...');

        const result = await testAIConnection(testRequest);

        // Refresh connections tree - receive params may have been updated
        eventBus.trigger(constants.EV_REFRESH_CONNECTION_NODES, constants.REST_GROUP_TYPE, constants.CONNECTION_REST_CONTAINER, 'rest');

        return result;
    }
}

export default new AIConnectionBuilder();
